import { Button } from '@/components/ui/button';
import { useState } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import { Eye, User, Heart, FileText, Users } from 'lucide-react';
import { CustomerDTO } from '@/hooks/useCustomer';
import { format } from 'date-fns';
import { vi } from 'date-fns/locale';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { renderGender, renderMaritalStatus } from '@/lib/utils';
import TextUserInactive from '@/components/TextUserInactive';

function Detail({ customer }: { customer: CustomerDTO }) {
  const [open, setOpen] = useState(false);

  // Format date if available
  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return format(new Date(dateString), 'dd/MM/yyyy', { locale: vi });
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="luxury-button-outline text-luxury-700"
        >
          <Eye className="w-4 h-4 text-luxury-600" />
          Chi tiết
        </Button>
      </DialogTrigger>
      <DialogContent className="min-w-[1000px] max-h-[95vh] overflow-y-auto luxury-theme">
        <DialogDescription className="sr-only">
          Chi tiết khách hàng
        </DialogDescription>
        <DialogHeader className="luxury-header">
          <DialogTitle className="luxury-title">
            Thông tin chi tiết khách hàng
          </DialogTitle>
        </DialogHeader>
        <div className="flex flex-col gap-8">
          {/* Thông tin cá nhân */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <User className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin cá nhân
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Họ và tên</span>
                  <span className="font-medium text-lg text-luxury-800">
                    {customer.fullName}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Giới tính</span>
                  <span className="font-medium text-luxury-800">
                    {renderGender(customer.gender)}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Số điện thoại</span>
                    <span className="font-medium text-luxury-800">
                      {customer.phoneNumber}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Email</span>
                    <span className="font-medium text-luxury-800">
                      {customer.email}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Ngày sinh</span>
                    <span className="font-medium text-luxury-800">
                      {formatDate(customer.dateOfBirth)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Địa chỉ làm việc</span>
                    <span className="font-medium text-luxury-800">
                      {customer.workAddress || '—'}
                    </span>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Địa chỉ nhà ở</span>
                    <span className="font-medium text-luxury-800">
                      {customer.address || '—'}
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Sở thích và phong cách */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <Heart className="w-5 h-5 mr-3 text-luxury-600" />
                Sở thích và phong cách
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Sở thích</span>
                  <span className="font-medium text-luxury-800">
                    {customer.hobbies || '—'}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Phong cách</span>
                  <span className="font-medium text-luxury-800">
                    {customer.style || '—'}
                  </span>
                </div>

                <div className="flex items-center space-x-3">
                  <div className="flex flex-col">
                    <span className="luxury-label">Màu yêu thích</span>
                    <div className="flex items-center space-x-2">
                      <span className="font-medium text-luxury-800">
                        {customer.favoriteColor || '—'}
                      </span>
                      {customer.favoriteColor && (
                        <div
                          className="w-4 h-4 rounded-full border border-luxury-200"
                          style={{ backgroundColor: customer.favoriteColor }}
                        />
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin gia đình */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <Users className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin gia đình
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Tình trạng hôn nhân</span>
                  <span className="font-medium text-luxury-800">
                    {renderMaritalStatus(customer.maritalStatus)}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Tên vợ/chồng</span>
                  <span className="font-medium text-luxury-800">
                    {customer.spouseName || '—'}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Ngày sinh vợ/chồng</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(customer.spouseBirthday)}
                  </span>
                </div>
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Số điện thoại vợ/chồng</span>
                  <span className="font-medium text-luxury-800">
                    {customer.phoneFamily || '—'}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin bổ sung */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <FileText className="w-5 h-5 mr-3 text-luxury-600" />
                Ghi chú
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Ghi chú</span>
                  <p className="font-medium whitespace-pre-wrap p-3 bg-luxury-50 rounded-md border border-luxury-100 text-luxury-800">
                    {customer.notes || '—'}
                  </p>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Ghi chú đặc biệt</span>
                  <p className="font-medium whitespace-pre-wrap p-3 bg-luxury-50 rounded-md border border-luxury-100 text-luxury-800">
                    {customer.specialNotes || '—'}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Thông tin hệ thống */}
          <Card className="luxury-card luxury-theme">
            <CardHeader className="pb-2 border-b border-luxury-100">
              <CardTitle className="text-lg flex items-center text-luxury-900">
                <FileText className="w-5 h-5 mr-3 text-luxury-600" />
                Thông tin hệ thống
              </CardTitle>
            </CardHeader>
            <CardContent className="pt-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Người bán hàng</span>
                  <span className="font-medium text-luxury-800">
                    {customer.seller?.name || '—'}
                    <TextUserInactive status={customer.seller?.status} />
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">ID</span>
                  <span className="font-medium text-sm font-mono bg-luxury-50 p-1 rounded border border-luxury-100 text-luxury-800">
                    {customer.id}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Ngày tạo</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(customer.createdAt)}
                  </span>
                </div>

                <div className="flex flex-col space-y-2">
                  <span className="luxury-label">Cập nhật lần cuối</span>
                  <span className="font-medium text-luxury-800">
                    {formatDate(customer.updatedAt)}
                  </span>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default Detail;
